import { useEffect, useRef, useState } from 'react';

import { WS_BASE_URL } from '../api/config';
import { authService } from '../services/auth';

type ModelLabEventsScope = string | number | null | undefined;

interface ModelLabEventMessage {
  type: string;
  payload?: Record<string, unknown>;
}

interface UseModelLabEventsSocketOptions {
  scope?: ModelLabEventsScope;
  enabled: boolean;
  onEvent?: (event: ModelLabEventMessage) => void;
  onReconnect?: () => void;
}

export const useModelLabEventsSocket = ({
  scope = 'all',
  enabled,
  onEvent,
  onReconnect,
}: UseModelLabEventsSocketOptions) => {
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const onEventRef = useRef(onEvent);
  const onReconnectRef = useRef(onReconnect);
  const hasConnectedRef = useRef(false);

  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  useEffect(() => {
    const normalizedScope = scope || 'all';
    const shouldConnect = Boolean(enabled && normalizedScope);
    let closedByEffect = false;

    const clearReconnectTimer = () => {
      if (reconnectTimerRef.current != null) {
        window.clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const closeSocket = () => {
      const socket = socketRef.current;
      socketRef.current = null;
      if (socket && socket.readyState !== WebSocket.CLOSED) {
        socket.close(1000, 'model lab events closed');
      }
    };

    if (!shouldConnect) {
      setIsConnected(false);
      clearReconnectTimer();
      closeSocket();
      return () => undefined;
    }

    const connect = () => {
      const token = authService.getAccessToken();
      if (!token || !WS_BASE_URL) {
        setIsConnected(false);
        return;
      }

      closeSocket();
      const socket = new WebSocket(
        `${WS_BASE_URL}/ws/model-lab/events/${normalizedScope}/?token=${encodeURIComponent(token)}`,
      );
      socketRef.current = socket;

      const wasReconnect = hasConnectedRef.current;
      socket.onopen = () => {
        setIsConnected(true);
        hasConnectedRef.current = true;
        if (wasReconnect) {
          onReconnectRef.current?.();
        }
      };
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as ModelLabEventMessage;
          if (message.type && message.type !== 'pong' && message.type !== 'model_lab.events.ready') {
            onEventRef.current?.(message);
          }
        } catch {
          // Ignore malformed payloads; HTTP queries remain the fallback.
        }
      };
      socket.onerror = () => setIsConnected(false);
      socket.onclose = () => {
        setIsConnected(false);
        if (!closedByEffect) {
          clearReconnectTimer();
          reconnectTimerRef.current = window.setTimeout(connect, 5000);
        }
      };
    };

    connect();

    return () => {
      closedByEffect = true;
      clearReconnectTimer();
      closeSocket();
      hasConnectedRef.current = false;
      setIsConnected(false);
    };
  }, [enabled, scope]);

  return { isConnected };
};

export default useModelLabEventsSocket;
